/* eslint-disable indent */
import styles from "./CourseCard.module.scss"
import { FC } from "react"
import { Trans, useTranslation } from "next-i18next"
import Image from "next/dist/client/image"
import CourseButton from "../course-button/CourseButton"
import { Course } from "interfaces/courses"
import Link from "next/dist/client/link"
import InfoPills from "../info-pill/InfoPills"
import { useProgress } from "@cs/lib/progress-v6/ProgressContext"
import SurveyDialog from "../survey-dialog/SurveyDialog"
import { MixpanelService } from "@cs/services/mixpanel"
import { useRouter } from "next/router"
import { localeToDashCase } from "@cs/lib/modLocaleToDashCase"

type CourseCardProps = Course & {
  useSurvey?: boolean
  isRelatedCourse?: boolean
}

const CourseCard: FC<CourseCardProps> = ({ useSurvey = false, isRelatedCourse, ...course }) => {
  const { t } = useTranslation("courses")
  const { progress } = useProgress()
  const router = useRouter()

  const trackClick = (started: boolean) => {
    MixpanelService.accessCourse({
      title: course.title || "",
      chapterId: "",
      courseId: course.id,
      version: "v6",
      started,
      isRelatedCourse,
    })
  }

  if ("description" in course) {
    const {
      title,
      description,
      lastUpdated,
      image,
      slug,
      timeToFinish,
      advancedChapters,
      id,
      simpleChapters,
    } = course as Course

    const SURVEY_KEY = `${slug}-v1.0`
    const surveyDone = typeof window !== "undefined" ? !!localStorage.getItem(SURVEY_KEY) : false
    const showSurvey = useSurvey ? (surveyDone ? false : true) : false
    const difficulty = router.query.difficulty
      ? (router.query.difficulty as "simple" | "advanced")
      : "both"
    const chaptersToEvaluate =
      difficulty == "both" || difficulty == "advanced" ? advancedChapters : simpleChapters

    const progressPercentage = Math.floor(
      (chaptersToEvaluate.filter(chapter =>
        chapter && progress.chapters
          ? Object.keys(progress.chapters).includes(chapter) &&
            progress.chapters[chapter].completedAt
          : false,
      ).length /
        (chaptersToEvaluate.length + 1)) *
        100,
    )

    const isStarted = !!progress?.courses?.[id]?.startedAt
    const isCompleted = !!progress?.courses?.[id]?.completedAt

    return (
      <article className={styles.card}>
        <header className={styles.header}>
          <figure className={styles.figure}>
            <Image
              src={image}
              layout="intrinsic"
              objectFit="cover"
              objectPosition={"center"}
              width={450}
              height={140}
              alt="Course Image"
              sizes="(max-width: 647px) 100vw,
              (max-width: 1020px) 350px,
              (max-width: 1300px) 33vw,
              25vw"
              quality={50}
            />
          </figure>
          <InfoPills calculateTimeWithPace students={undefined} timeToFinish={timeToFinish} />
        </header>
        <div className={styles.content}>
          <h3 className={styles.h3}>{title}</h3>
          <p className={styles.p}>{description}</p>
          <p className={styles.date}>
            <Trans
              t={t}
                i18nKey="updated-on"
                values={{
                  date: new Date(lastUpdated).toLocaleDateString(
                    localeToDashCase(router.locale),
                    { weekday: "long", year: "numeric", month: "long", day: "numeric" },
                  ),
                }}
              />
          </p>
        </div>
        <footer className={styles.footer}>
          {showSurvey ? (
            <SurveyDialog courseLink={`/new-courses/${slug}`} surveyKey={SURVEY_KEY} />
          ) : (
            <Link
              href={`/new-courses/${slug}${
                router.query.difficulty ? `?difficulty=${router.query.difficulty}` : ""
              }`}
            >
              <CourseButton
                onClick={() => trackClick(progressPercentage > 0)}
                // TODO: bugfix when progress === 0, the bar is showing completed
                progress={
                  isCompleted ? 100 : progressPercentage === 0 && isStarted ? 5 : progressPercentage
                }
                className={styles.button}
              >
                {!isStarted ? t("start-learning") : isCompleted ? t("completed") : t("continue")}
              </CourseButton>
            </Link>
          )}
        </footer>
      </article>
    )
  }

  return <></>
}

export default CourseCard
